import { SEQUENTIAL_BLUE, sequentialStep } from "./palette";
import { BOSH_USLUB } from "./scale";

export interface HeatGridAxisItem {
	key: string;
	label: string;
}

export interface HeatGridCell {
	row: string;
	column: string;
	value: number;
	tooltip: string;
}

export interface HeatGridProps {
	rows: HeatGridAxisItem[];
	columns: HeatGridAxisItem[];
	cells: HeatGridCell[];
	/** Berilmasa — kataklardagi eng katta qiymat. */
	max?: number;
	/** Katak ichida sanoqni ko'rsatish (nol kataklarda chiqmaydi). */
	showValues?: boolean;
	legendMinLabel: string;
	legendMaxLabel: string;
	/** Qator yoki ustun yo'q bo'lganda matn (reja imzosidan kengaytirilgan). */
	empty?: string;
	/** Berilmasa — nol bo'lmagan kataklarning tooltip'lari (reja imzosidan kengaytirilgan). */
	ariaLabel?: string;
}

/** Ramp'ning shu qadamidan boshlab kataklar to'q — matn oq. */
const TOQ_QADAM = 5;
const OQ = "#ffffff";
/**
 * Och ramp katagidagi matn — qat'iy to'q hex, `var(--chart-ink)` EMAS.
 * Ramp foni mavzuga bog'liq emas (faqat light qiymat), `--chart-ink`
 * esa dark rejimda #e6e8eb bo'ladi: #cde2fb ustida ~1.2:1 kontrast.
 */
const INK_OCH_FONDA = "#14171c";

function kalit(row: string, column: string): string {
	return `${row}|${column}`;
}

/**
 * Issiqlik xaritasi (sof CSS grid) — masalan hafta kuni × soat.
 *
 * ⚠️ **Nol / yo'q katak — `var(--chart-grid)`, `SEQUENTIAL_BLUE[0]`
 * EMAS.** Ramp faqat light qiymatli (`palette.ts`); nol katak dark
 * rejimda och kulrang dog' bo'lib qolardi. Noldan katta kataklar ramp
 * rangida qoladi — ular ma'no tashiydi. Shu sababli ulardagi raqam
 * rangi ham mavzudan mustaqil (`OQ` / `INK_OCH_FONDA`); nol katakda
 * raqam chiqmaydi.
 *
 * ⚠️ **Yo'q juftlik ham katak** — jadval to'rtburchak qoladi va uning
 * `title` i «0» ni so'z bilan aytadi.
 *
 * ⚠️ `data-heat-cell` / `data-row` / `data-column` — TEST KONTRAKTI.
 */
export function HeatGrid({
	rows,
	columns,
	cells,
	max,
	showValues = false,
	legendMinLabel,
	legendMaxLabel,
	empty,
	ariaLabel,
}: HeatGridProps) {
	if (rows.length === 0 || columns.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const xarita = new Map<string, HeatGridCell>();
	for (const katak of cells) {
		xarita.set(kalit(katak.row, katak.column), katak);
	}

	const shkala = max ?? Math.max(0, ...cells.map((k) => k.value));
	const yorliq =
		ariaLabel ??
		cells
			.filter((k) => k.value > 0)
			.map((k) => k.tooltip)
			.join(", ");

	return (
		<div>
			<div
				role="img"
				aria-label={yorliq}
				className="grid gap-0.5 text-[10px]"
				style={{ gridTemplateColumns: `auto repeat(${columns.length}, minmax(0, 1fr))` }}
			>
				<div />
				{columns.map((ustun) => (
					<div
						key={ustun.key}
						className="truncate text-center tabular-nums"
						style={{ color: "var(--chart-axis)" }}
					>
						{ustun.label}
					</div>
				))}

				{rows.map((qator) => (
					<HeatRow
						key={qator.key}
						qator={qator}
						columns={columns}
						xarita={xarita}
						shkala={shkala}
						showValues={showValues}
					/>
				))}
			</div>

			<div
				className="mt-2 flex items-center justify-end gap-1 text-[10px]"
				style={{ color: "var(--chart-axis)" }}
			>
				<span>{legendMinLabel}</span>
				{SEQUENTIAL_BLUE.slice(1).map((rang) => (
					<span
						key={rang}
						aria-hidden="true"
						className="inline-block h-2.5 w-3 rounded-sm"
						style={{ backgroundColor: rang }}
					/>
				))}
				<span>{legendMaxLabel}</span>
			</div>
		</div>
	);
}

function HeatRow({
	qator,
	columns,
	xarita,
	shkala,
	showValues,
}: {
	qator: HeatGridAxisItem;
	columns: HeatGridAxisItem[];
	xarita: Map<string, HeatGridCell>;
	shkala: number;
	showValues: boolean;
}) {
	return (
		<>
			<div className="truncate pr-1.5 leading-5" style={{ color: "var(--chart-axis)" }}>
				{qator.label}
			</div>
			{columns.map((ustun) => {
				const katak = xarita.get(kalit(qator.key, ustun.key));
				const qiymat = katak?.value ?? 0;
				const bor = qiymat > 0;
				const rang = bor ? sequentialStep(qiymat, shkala) : "var(--chart-grid)";
				const toq = bor && SEQUENTIAL_BLUE.indexOf(rang) >= TOQ_QADAM;

				return (
					<div
						key={ustun.key}
						data-heat-cell
						data-row={qator.key}
						data-column={ustun.key}
						title={katak?.tooltip ?? `${qator.label} · ${ustun.label}: 0`}
						className="flex h-5 min-w-0 items-center justify-center rounded-sm tabular-nums"
						style={{ backgroundColor: rang, color: toq ? OQ : INK_OCH_FONDA }}
					>
						{showValues && bor ? qiymat : null}
					</div>
				);
			})}
		</>
	);
}
